import { useLayoutEffect } from 'react';
import { useDispatch } from 'react-redux';
import { onInitOneSignalAction } from '@/domain/chat/chatActions';
import { getRuntimeConfig } from '@/lib/runtimeConfig';

import { useTheme } from '@mui/material';

const LOG = '[OneSignal]';

const logState = (label) => {
  const { OneSignal } = window;
  if (!OneSignal || typeof OneSignal.isPushNotificationsEnabled !== 'function') {
    console.log(LOG, label, 'SDK not ready for state query');
    return;
  }
  // Each call is wrapped in Promise.resolve().then(...) so a synchronous throw
  // (e.g. method is missing or throws before returning a Promise) is caught
  // by .catch() instead of bubbling up uncaught. Returns null for missing APIs.
  const safeCall = (fn) =>
    typeof fn === 'function'
      ? Promise.resolve()
          .then(() => fn.call(OneSignal))
          .catch((e) => ({ err: String(e) }))
      : Promise.resolve(null);

  Promise.all([
    safeCall(OneSignal.isPushNotificationsEnabled),
    safeCall(OneSignal.getUserId),
    safeCall(OneSignal.getExternalUserId),
    safeCall(OneSignal.getNotificationPermission),
    safeCall(OneSignal.getSubscription),
  ])
    .then(([enabled, userId, externalUserId, permission, subscription]) => {
      console.log(LOG, label, {
        pushEnabled: enabled,
        oneSignalUserId: userId,
        externalUserId,
        permission,
        subscription,
        documentVisibility: document.visibilityState,
      });
    })
    .catch((err) => console.warn(LOG, 'state query failed', label, err));
};

const OneSignalInit = () => {
  const dispatch = useDispatch();
  const {
    labelOptions: { onesignalAppId },
  } = useTheme();

  useLayoutEffect(() => {
    window.OneSignal = window.OneSignal || [];
    const { OneSignal } = window;
    // process.env.REACT_APP_ONESIGNAL_APP_ID  is set on dev/release envs only.
    // prod env should not have this variable set as prod keys are retrieved from WL config
    const appId = getRuntimeConfig().env.REACT_APP_ONESIGNAL_APP_ID
      ? getRuntimeConfig().env.REACT_APP_ONESIGNAL_APP_ID
      : onesignalAppId;
    // Console log args are kept as positional (const-prefix, static label, payload)
    // so the first argument is always a static literal — no user-controlled value
    // is ever interpreted as a printf-style format string (SAST: "unsafe formatstring").
    console.log(LOG, 'init starting', { appId, href: window.location.href });
    // we initializa SDK for every env except for local, not to get configuraion error
    // as we dont have app allowed on localhost
    // it will anyway work on allowed domains only and wont work on dynamic domains like PR
    OneSignal.push(() => {
      // OneSignal.init() is async and can reject (e.g. checkRestrictedOrigin
      // on local dev against the prod app id). Register subscribe + observers
      // ONLY after init resolves so we never call into an unready SDK and
      // never leave the init promise rejection unhandled.
      OneSignal.init({
        appId,
        allowLocalhostAsSecureOrigin: true,
        autoResubscribe: true,
      })
        .then(() => {
          console.log(LOG, 'SDK init() complete');

          // Force-enable subscription regardless of chat version.
          //
          // Previously this was only called from legacy chatMiddleware's
          // initPushNotificationObserver(), which short-circuits in v4 chat
          // mode (isNewChatSelector → enable_chat_v2 flag). That short-circuit
          // silently broke push notification delivery for organizations on
          // chat v2 because no code path ever called setSubscription(true)
          // again.
          OneSignal.setSubscription(true);
          console.log(LOG, 'setSubscription(true) requested');

          // Foreground display handler — fires for every push delivered while
          // a tab is open (regardless of OS-level notification visibility).
          // Logging this lets us prove the push reached the SDK even if
          // Chrome chose not to render a system notification (which it
          // suppresses by default while the page is focused).
          OneSignal.on('notificationDisplay', (event) => {
            console.log(LOG, 'notificationDisplay', {
              visibility: document.visibilityState,
              event,
            });
          });

          // Re-subscribe on permission grant — same behavior the legacy
          // observer had, but now runs in v4 mode too.
          OneSignal.on('notificationPermissionChange', (change) => {
            console.log(LOG, 'notificationPermissionChange', change);
            if (change.to === 'granted') {
              OneSignal.setSubscription(true);
              console.log(LOG, 'setSubscription(true) re-requested after permission grant');
            }
          });

          OneSignal.on('subscriptionChange', (isSubscribed) => {
            console.log(LOG, 'subscriptionChange', { isSubscribed });
            logState('state');
          });

          // Defer the first state query — getUserId / subscription state may
          // still be resolving at this exact tick. Spec contract: label is
          // `[OneSignal] state` so QA can grep for it post-deploy.
          setTimeout(() => logState('state'), 1000);
        })
        .catch((err) => {
          // Restricted origin / network failure / misconfig: surface with the
          // standard prefix and DO NOT throw — app must keep running.
          console.warn(LOG, 'SDK init() failed', err);
        });

      // Legacy chat (v3) init dispatch is intentionally OUTSIDE the
      // init-promise chain: legacy chat setup must run regardless of whether
      // OneSignal init succeeded (legacy Sendbird WS does not depend on push).
      dispatch(onInitOneSignalAction());
    });

    const onVisibilityChange = () => {
      console.log(LOG, 'visibilityChange', { state: document.visibilityState });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dispatch, onesignalAppId]);

  return null;
};

export default OneSignalInit;
